import { useState, useRef, useEffect, useCallback } from 'react';
import { sanitizeHtml } from '@/lib/sanitizeHtml';
import { MessageDeliveryBadge } from '@/components/portal/MessageDeliveryBadge';
import { Mail, Send, X, Minus, MessageCircle, MapPin, Calendar, Briefcase, Smartphone, Radio, FileText, ChevronLeft } from 'lucide-react';
import { useChatContext, type ChatConversation } from '@/contexts/ChatContext';
import { useOfficePortalMessages, useOfficeSendPortalMessage, useOfficeSendPortalEmail, useOfficeSendSms } from '@/hooks/useOfficePortalMessages';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@/components/ui/hover-card';
import { MessageTemplatePicker } from '@/components/office/MessageTemplatePicker';
import { toast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { scopedKey } from '@/lib/scopedStorage';

const BUBBLE_KEY_BASE = 'chat_popup_pos';
const PANEL_KEY_BASE = 'chat_panel_pos';

// ── Single conversation panel ──
function ConversationPanel({ conv }: { conv: ChatConversation }) {
  const [channel, setChannel] = useState<'portal' | 'email' | 'sms' | 'all'>('portal');
  const [newMessage, setNewMessage] = useState('');
  const [emailSubject, setEmailSubject] = useState('');
  const [customPhone, setCustomPhone] = useState('');
  const scrollRef = useRef<HTMLDivElement>(null);

  const { data: portalMessages, refetch: refetchPortalMessages } = useOfficePortalMessages(conv.projectId, conv.customerId);
  const sendPortalMessage = useOfficeSendPortalMessage();
  const sendPortalEmail = useOfficeSendPortalEmail();
  const sendSms = useOfficeSendSms();

  const portalMsgList = portalMessages || [];
  const isSending = sendPortalMessage.isPending || sendPortalEmail.isPending || sendSms.isPending;

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [portalMsgList.length]);

  const handleSend = () => {
    if (!newMessage.trim() || !conv.customerId) return;
    const msg = newMessage.trim();
    const onDone = () => { setNewMessage(''); refetchPortalMessages(); };

    if (channel === 'all') {
      // Broadcast to all channels
      sendPortalMessage.mutate(
        { projectId: conv.projectId, customerId: conv.customerId, message: msg },
        { onSuccess: () => refetchPortalMessages() }
      );
      sendPortalEmail.mutate(
        { projectId: conv.projectId, customerId: conv.customerId, message: msg },
        {}
      );
      if (conv.customerPhone || customPhone.trim()) {
        sendSms.mutate(
          { projectId: conv.projectId, customerId: conv.customerId, message: msg, recipientPhone: customPhone.trim() || conv.customerPhone! },
          { onError: (e: any) => toast({ title: 'SMS fejlede', description: e.message, variant: 'destructive' }) }
        );
      }
      setNewMessage('');
    } else if (channel === 'email') {
      sendPortalEmail.mutate(
        { projectId: conv.projectId, customerId: conv.customerId, message: msg, subject: emailSubject.trim() || undefined },
        { onSuccess: () => { setNewMessage(''); setEmailSubject(''); setChannel('portal'); refetchPortalMessages(); } }
      );
    } else if (channel === 'sms') {
      const phone = customPhone.trim() || conv.customerPhone;
      if (!phone) return;
      sendSms.mutate(
        { projectId: conv.projectId, customerId: conv.customerId, message: msg, recipientPhone: phone },
        { onSuccess: onDone, onError: (e: any) => toast({ title: 'SMS fejlede', description: e.message, variant: 'destructive' }) }
      );
    } else {
      sendPortalMessage.mutate(
        { projectId: conv.projectId, customerId: conv.customerId, message: msg },
        { onSuccess: onDone }
      );
    }
  };

  return (
    <div className="flex flex-col flex-1 min-h-0">
      {/* Messages */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-3 py-2">
        {portalMsgList.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <Mail className="w-8 h-8 text-muted-foreground/30 mb-2" />
            <p className="text-xs text-muted-foreground">Ingen beskeder endnu</p>
          </div>
        ) : (
          <div className="space-y-0.5">
            {portalMsgList.map((msg: any, idx: number) => {
              const isEmail = msg.message_type === 'email_sent' || msg.message_type === 'email_received';
              const isSms = msg.message_type === 'sms_sent' || msg.message_type === 'sms_received';
              // Direction is derived from message_type for SMS/email so they
              // render correctly even if sender_type is missing or unexpected.
              const isStaff = msg.message_type === 'sms_sent' || msg.message_type === 'email_sent'
                ? true
                : msg.message_type === 'sms_received' || msg.message_type === 'email_received'
                  ? false
                  : msg.sender_type === 'staff';
              const effectiveSender = isStaff ? 'staff' : 'customer';
              const prevMsg = idx > 0 ? portalMsgList[idx - 1] : null;
              const prevEffective = prevMsg
                ? (prevMsg.message_type === 'sms_sent' || prevMsg.message_type === 'email_sent'
                    ? 'staff'
                    : prevMsg.message_type === 'sms_received' || prevMsg.message_type === 'email_received'
                      ? 'customer'
                      : (prevMsg.sender_type === 'staff' ? 'staff' : 'customer'))
                : null;
              const sameSender = prevEffective && prevEffective === effectiveSender;
              const timeDiff = prevMsg ? Math.abs(new Date(msg.created_at).getTime() - new Date(prevMsg.created_at).getTime()) : Infinity;
              const showTime = timeDiff > 300000;
              const showSender = !sameSender || showTime;
              const nextMsg = idx < portalMsgList.length - 1 ? portalMsgList[idx + 1] : null;
              const nextEffective = nextMsg
                ? (nextMsg.message_type === 'sms_sent' || nextMsg.message_type === 'email_sent'
                    ? 'staff'
                    : nextMsg.message_type === 'sms_received' || nextMsg.message_type === 'email_received'
                      ? 'customer'
                      : (nextMsg.sender_type === 'staff' ? 'staff' : 'customer'))
                : null;
              const isLastInGroup = !nextMsg || nextEffective !== effectiveSender || (nextMsg && Math.abs(new Date(nextMsg.created_at).getTime() - new Date(msg.created_at).getTime()) > 300000);
              const senderInitial = isStaff ? (msg.sender_name?.[0] || 'K').toUpperCase() : (msg.sender_name?.[0] || conv.customerName?.[0] || 'K').toUpperCase();

              return (
                <div key={msg.id}>
                  {showTime && idx > 0 && (
                    <div className="flex justify-center my-2">
                      <span className="text-[9px] text-muted-foreground/50 bg-muted/30 px-2 py-0.5 rounded-full">
                        {new Date(msg.created_at).toLocaleString('da-DK', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>
                  )}
                  <div className={`flex ${isStaff ? 'justify-end' : 'justify-start'} ${showSender ? 'mt-2.5' : 'mt-0.5'}`}>
                    <div className={`flex items-end gap-1.5 ${isStaff ? 'flex-row-reverse' : 'flex-row'} max-w-[85%]`}>
                      {isLastInGroup ? (
                        <div className={`w-6 h-6 rounded-full shrink-0 flex items-center justify-center text-[9px] font-bold ${
                          isStaff ? 'bg-primary text-primary-foreground' : 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300'
                        }`}>
                          {senderInitial}
                        </div>
                      ) : (
                        <div className="w-6 shrink-0" />
                      )}
                      <div className={`flex flex-col min-w-0 ${isStaff ? 'items-end' : 'items-start'}`}>
                        {showSender && (
                          <span className={`text-[9px] font-medium text-muted-foreground/60 mb-0.5 ${isStaff ? 'mr-0.5' : 'ml-0.5'}`}>
                            {isStaff ? (msg.sender_name || 'Kontor') : (msg.sender_name || conv.customerName)}
                          </span>
                        )}
                        <div className={`rounded-2xl px-3 py-1.5 text-[12px] leading-relaxed min-w-0 ${
                          isStaff
                            ? isEmail ? 'bg-blue-600 text-white rounded-br-sm'
                              : isSms ? 'bg-green-600 text-white rounded-br-sm'
                              : 'bg-foreground text-background rounded-br-sm'
                            : isEmail ? 'bg-emerald-50 text-foreground border border-emerald-200/60 dark:bg-emerald-950/20 dark:border-emerald-800/40 rounded-bl-sm'
                              : isSms ? 'bg-green-50 text-foreground border border-green-200/60 dark:bg-green-950/20 dark:border-green-800/40 rounded-bl-sm'
                              : 'bg-muted text-foreground rounded-bl-sm'
                        }`}>
                          {isEmail && (
                            <div className={`flex items-center gap-1 mb-0.5 text-[9px] ${isStaff ? 'text-white/60' : 'text-emerald-600 dark:text-emerald-400'}`}>
                              <Mail className="w-2.5 h-2.5" />
                              <span>{msg.message_type === 'email_sent' ? 'Email' : 'Modtaget'}</span>
                              {msg.message_type === 'email_sent' && msg.metadata?.message_id && (
                                <MessageDeliveryBadge messageId={msg.metadata.message_id} variant={isStaff ? 'dark' : 'light'} />
                              )}
                            </div>
                          )}
                          {isSms && (
                            <div className={`flex items-center gap-1 mb-0.5 text-[9px] ${isStaff ? 'text-white/60' : 'text-green-600 dark:text-green-400'}`}>
                              <Smartphone className="w-2.5 h-2.5" />
                              <span>{msg.message_type === 'sms_sent' ? 'SMS' : 'SMS modtaget'}</span>
                            </div>
                          )}
                          {isEmail && msg.metadata?.html_body ? (
                            <div
                              className="prose prose-sm max-w-none [&_*]:!text-inherit"
                              style={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}
                              dangerouslySetInnerHTML={{ __html: sanitizeHtml(msg.metadata.html_body) }}
                            />
                          ) : (
                            <p className="whitespace-pre-wrap" style={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}>{msg.message}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Compose */}
      <div className="border-t border-border px-3 py-2 bg-card">
        <div className="flex items-center gap-1.5 mb-1.5 flex-wrap">
          {(['portal', 'email', 'sms', 'all'] as const).map(ch => {
            const icons = { portal: MessageCircle, email: Mail, sms: Smartphone, all: Radio };
            const labels = { portal: 'Portal', email: 'Email', sms: 'SMS', all: 'Alle' };
            const colors = { portal: 'bg-primary text-primary-foreground', email: 'bg-blue-600 text-white', sms: 'bg-green-600 text-white', all: 'bg-amber-600 text-white' };
            const Icon = icons[ch];
            // SMS button always visible so user can enter a custom number
            return (
              <button
                key={ch}
                onClick={() => setChannel(ch)}
                className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-semibold transition-all ${
                  channel === ch ? `${colors[ch]} shadow-sm` : 'bg-muted/60 text-muted-foreground hover:text-foreground hover:bg-muted'
                }`}
              >
                <Icon className="w-3 h-3" />
                {labels[ch]}
              </button>
            );
          })}
          <MessageTemplatePicker channel={channel} onSelect={(body) => setNewMessage(body)} />
        </div>
        {channel === 'email' && (
          <input
            type="text"
            value={emailSubject}
            onChange={(e) => setEmailSubject(e.target.value)}
            placeholder="Emne (valgfrit)"
            className="h-8 px-3 mb-1 rounded-lg border border-border bg-muted/30 text-xs placeholder:text-muted-foreground/50 focus:outline-none focus:ring-2 focus:ring-ring/20 focus:bg-background transition-all w-full"
          />
        )}
        {channel === 'sms' && (
          <div className="flex items-center gap-1.5 mb-1">
            <Smartphone className="w-3.5 h-3.5 text-green-600 shrink-0" />
            <input
              type="tel"
              value={customPhone}
              onChange={(e) => setCustomPhone(e.target.value)}
              placeholder={conv.customerPhone || 'Indtast telefonnummer'}
              className="flex-1 h-8 px-3 rounded-lg border border-border bg-muted/30 text-xs placeholder:text-muted-foreground/50 focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:bg-background transition-all"
            />
          </div>
        )}
        <div className="flex gap-1.5 items-end">
          <textarea
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            rows={Math.min(8, Math.max(1, (newMessage.match(/\n/g)?.length || 0) + 1))}
            placeholder={
              channel === 'all' ? 'Besked til alle kanaler... (Cmd/Ctrl+Enter for at sende)'
                : channel === 'email' ? 'Email til kunden... (Cmd/Ctrl+Enter for at sende)'
                : channel === 'sms' ? 'SMS til kunden... (Cmd/Ctrl+Enter for at sende)'
                : 'Besked til kunden... (Cmd/Ctrl+Enter for at sende)'
            }
            className="flex-1 min-h-9 px-3 py-2 rounded-2xl border border-border bg-muted/30 text-sm placeholder:text-muted-foreground/50 focus:outline-none focus:ring-2 focus:ring-ring/20 focus:bg-background transition-all resize-none whitespace-pre-wrap"
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                handleSend();
              }
            }}
          />
          <button
            onClick={handleSend}
            disabled={isSending || !newMessage.trim() || (channel === 'sms' && !conv.customerPhone && !customPhone.trim())}
            className={`h-9 w-9 rounded-full flex items-center justify-center shrink-0 transition-all disabled:opacity-20 ${
              channel === 'email' ? 'bg-blue-600 text-white hover:bg-blue-700'
                : channel === 'sms' ? 'bg-green-600 text-white hover:bg-green-700'
                : channel === 'all' ? 'bg-amber-600 text-white hover:bg-amber-700'
                : 'bg-primary text-primary-foreground hover:opacity-90'
            }`}
          >
            {isSending ? (
              <div className="w-3.5 h-3.5 border-2 border-current border-t-transparent rounded-full animate-spin" />
            ) : channel === 'all' ? (
              <Radio className="w-3.5 h-3.5" />
            ) : (
              <Send className="w-3.5 h-3.5" />
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main popup shell with tabs ──
function getSavedPos(key: string): { x: number; y: number } | null {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

export function CaseChatPopup({ onBack }: { onBack?: () => void } = {}) {
  const {
    conversations,
    activeConversationId,
    isOpen,
    isMinimized,
    setIsOpen,
    setIsMinimized,
    setActiveConversation,
    removeConversation,
  } = useChatContext();
  const { user } = useAuth();
  const BUBBLE_STORAGE_KEY = scopedKey(BUBBLE_KEY_BASE, user?.id, user?.company_id);
  const PANEL_STORAGE_KEY = scopedKey(PANEL_KEY_BASE, user?.id, user?.company_id);

  // ── Bubble drag state (same pattern as BugReportButton) ──
  const bubbleRef = useRef<HTMLButtonElement>(null);
  const bubbleDragging = useRef(false);
  const bubbleOffset = useRef({ x: 0, y: 0 });
  const bubbleMoved = useRef(false);
  const [bubblePos, setBubblePos] = useState<{ x: number; y: number } | null>(() => getSavedPos(BUBBLE_STORAGE_KEY));

  const clampBubble = useCallback((x: number, y: number) => ({
    x: Math.max(0, Math.min(window.innerWidth - 40, x)),
    y: Math.max(0, Math.min(window.innerHeight - 40, y)),
  }), []);

  const onBubblePointerDown = useCallback((e: React.PointerEvent) => {
    bubbleDragging.current = true;
    bubbleMoved.current = false;
    const rect = bubbleRef.current!.getBoundingClientRect();
    bubbleOffset.current = { x: e.clientX - rect.left, y: e.clientY - rect.top };
    bubbleRef.current!.setPointerCapture(e.pointerId);
    e.preventDefault();
  }, []);

  const onBubblePointerMove = useCallback((e: React.PointerEvent) => {
    if (!bubbleDragging.current) return;
    bubbleMoved.current = true;
    setBubblePos(clampBubble(e.clientX - bubbleOffset.current.x, e.clientY - bubbleOffset.current.y));
  }, [clampBubble]);

  const onBubblePointerUp = useCallback(() => {
    if (!bubbleDragging.current) return;
    bubbleDragging.current = false;
    if (bubbleMoved.current) {
      setBubblePos(prev => { if (prev) localStorage.setItem(BUBBLE_STORAGE_KEY, JSON.stringify(prev)); return prev; });
    } else {
      setIsOpen(true);
      setIsMinimized(false);
    }
  }, [setIsOpen, setIsMinimized]);

  // ── Panel drag state ──
  const panelDragging = useRef(false);
  const panelOffset = useRef({ x: 0, y: 0 });
  const panelMoved = useRef(false);
  const [panelPos, setPanelPos] = useState<{ x: number; y: number } | null>(() => getSavedPos(PANEL_STORAGE_KEY));

  const clampPanel = useCallback((x: number, y: number) => ({
    x: Math.max(0, Math.min(window.innerWidth - 380, x)),
    y: Math.max(0, Math.min(window.innerHeight - 480, y)),
  }), []);

  const onPanelPointerDown = useCallback((e: React.PointerEvent) => {
    const target = e.target as HTMLElement;
    if (target.closest('button, a, input, textarea, [data-no-drag]')) return;
    panelDragging.current = true;
    panelMoved.current = false;
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    panelOffset.current = { x: e.clientX - rect.left, y: e.clientY - rect.top };
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  }, []);

  const onPanelPointerMove = useCallback((e: React.PointerEvent) => {
    if (!panelDragging.current) return;
    panelMoved.current = true;
    setPanelPos(clampPanel(e.clientX - panelOffset.current.x, e.clientY - panelOffset.current.y));
  }, [clampPanel]);

  const onPanelPointerUp = useCallback((e: React.PointerEvent) => {
    if (!panelDragging.current) return;
    panelDragging.current = false;
    (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId);
    if (panelMoved.current) {
      setPanelPos(prev => { if (prev) localStorage.setItem(PANEL_STORAGE_KEY, JSON.stringify(prev)); return prev; });
    }
  }, []);

  const activeConv = conversations.find(c => c.customerId === activeConversationId);

  const handleClosePopup = () => {
    if (conversations.length <= 1 && activeConversationId) {
      removeConversation(activeConversationId);
      return;
    }
    setIsOpen(false);
    setIsMinimized(false);
  };

  // When embedded via onBack, force open
  useEffect(() => {
    if (onBack && (!isOpen || isMinimized)) {
      setIsOpen(true);
      setIsMinimized(false);
    }
  }, [onBack, isOpen, isMinimized, setIsOpen, setIsMinimized]);

  if (conversations.length === 0 && !onBack) return null;
  if (conversations.length === 0 && onBack) {
    return (
      <div className="fixed bottom-4 right-4 z-50 w-[320px] bg-background border border-border rounded-xl shadow-2xl p-6 text-center">
        <MessageCircle className="w-10 h-10 text-muted-foreground/20 mx-auto mb-3" />
        <p className="text-sm font-medium text-muted-foreground">Ingen samtaler åbne</p>
        <p className="text-xs text-muted-foreground/60 mt-1">Åbn en sag for at starte en samtale</p>
        <button onClick={onBack} className="mt-3 text-xs text-primary hover:underline">← Tilbage</button>
      </div>
    );
  }

  const bubbleStyle: React.CSSProperties = bubblePos
    ? { position: 'fixed', left: bubblePos.x, top: bubblePos.y, right: 'auto', bottom: 'auto' }
    : { position: 'fixed', bottom: 80, right: 16 };

  const panelStyle: React.CSSProperties = panelPos
    ? { position: 'fixed', left: panelPos.x, top: panelPos.y, right: 'auto', bottom: 'auto', height: '480px' }
    : { position: 'fixed', bottom: 24, right: 24, height: '480px' };

  // ── Floating bubble (only when not embedded via onBack) ──
  if ((!isOpen || isMinimized) && !onBack) {
    return (
      <button
        ref={bubbleRef}
        onPointerDown={onBubblePointerDown}
        onPointerMove={onBubblePointerMove}
        onPointerUp={onBubblePointerUp}
        className="z-[9999] h-10 w-10 rounded-full bg-primary text-primary-foreground shadow-lg hover:scale-110 transition-all flex items-center justify-center animate-in zoom-in-50 duration-300 touch-none cursor-grab active:cursor-grabbing select-none"
        style={{ ...bubbleStyle, width: 40, height: 40 }}
      >
        <MessageCircle className="h-4 w-4 pointer-events-none" />
        {conversations.length > (isMinimized ? 0 : 1) && (
          <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-destructive text-destructive-foreground text-[10px] font-bold flex items-center justify-center pointer-events-none">
            {conversations.length}
          </span>
        )}
      </button>
    );
  }

  // ── Full popup ──
  return (
    <div className="z-50 w-[380px] rounded-xl bg-card border border-border shadow-2xl flex flex-col overflow-hidden" style={panelStyle}>
      {/* Header — drag handle */}
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-border bg-card cursor-grab active:cursor-grabbing select-none touch-none" onPointerDown={onPanelPointerDown} onPointerMove={onPanelPointerMove} onPointerUp={onPanelPointerUp}>
        <div className="flex items-center gap-1">
          {onBack && (
            <button onClick={onBack} className="w-7 h-7 rounded-full hover:bg-secondary flex items-center justify-center transition-colors pointer-events-auto" data-no-drag>
              <ChevronLeft className="w-4 h-4 text-muted-foreground" />
            </button>
          )}
        <HoverCard openDelay={200} closeDelay={100}>
          <HoverCardTrigger asChild>
            <div className="flex items-center gap-2.5 pointer-events-none">
              <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-bold">
                {activeConv ? (activeConv.customerName[0]?.toUpperCase() || 'K') : <MessageCircle className="w-4 h-4" />}
              </div>
              <div>
                <p className="text-sm font-semibold text-foreground leading-tight">
                  {activeConv ? activeConv.customerName : 'Beskeder'}
                </p>
                {activeConv?.customerEmail && <p className="text-[10px] text-muted-foreground">{activeConv.customerEmail}</p>}
              </div>
            </div>
          </HoverCardTrigger>
          {activeConv && (
            <HoverCardContent side="top" align="start" className="w-64 p-0 overflow-hidden">
              <div className="px-3 py-2.5 bg-muted/40 border-b border-border">
                <p className="text-xs font-semibold text-foreground truncate">{activeConv.projectTitle || 'Sag'}</p>
                {activeConv.projectStatus && (
                  <span className="inline-block mt-1 px-2 py-0.5 rounded-full bg-primary/10 text-primary text-[10px] font-medium">
                    {activeConv.projectStatus}
                  </span>
                )}
              </div>
              <div className="px-3 py-2 space-y-1.5">
                {activeConv.projectAddress && (
                  <div className="flex items-start gap-2">
                    <MapPin className="w-3 h-3 text-muted-foreground mt-0.5 shrink-0" />
                    <span className="text-[11px] text-muted-foreground leading-tight">{activeConv.projectAddress}</span>
                  </div>
                )}
                {activeConv.projectCreatedAt && (
                  <div className="flex items-center gap-2">
                    <Calendar className="w-3 h-3 text-muted-foreground shrink-0" />
                    <span className="text-[11px] text-muted-foreground">
                      Oprettet {new Date(activeConv.projectCreatedAt).toLocaleDateString('da-DK', { day: 'numeric', month: 'short', year: 'numeric' })}
                    </span>
                  </div>
                )}
                {activeConv.projectStartDate && (
                  <div className="flex items-center gap-2">
                    <Briefcase className="w-3 h-3 text-muted-foreground shrink-0" />
                    <span className="text-[11px] text-muted-foreground">
                      Start {new Date(activeConv.projectStartDate).toLocaleDateString('da-DK', { day: 'numeric', month: 'short', year: 'numeric' })}
                    </span>
                  </div>
                )}
              </div>
            </HoverCardContent>
          )}
        </HoverCard>
        </div>
        <div className="flex items-center gap-0.5 pointer-events-auto">
          <button onClick={() => setIsMinimized(true)} className="w-7 h-7 rounded-full hover:bg-secondary flex items-center justify-center transition-colors">
            <Minus className="w-4 h-4 text-muted-foreground" />
          </button>
          <button onClick={handleClosePopup} className="w-7 h-7 rounded-full hover:bg-secondary flex items-center justify-center transition-colors">
            <X className="w-4 h-4 text-muted-foreground" />
          </button>
        </div>
      </div>

      {/* Conversation tabs */}
      {conversations.length > 1 && (
        <div className="flex items-center gap-1 px-2 py-1.5 border-b border-border bg-muted/30 overflow-x-auto scrollbar-none">
          {conversations.map(c => (
            <button
              key={c.customerId}
              onClick={() => setActiveConversation(c.customerId)}
              className={`group flex items-center gap-1 px-2.5 py-1 rounded-lg text-[11px] font-medium whitespace-nowrap transition-all shrink-0 ${
                c.customerId === activeConversationId
                  ? 'bg-primary text-primary-foreground shadow-sm'
                  : 'bg-card text-muted-foreground hover:text-foreground hover:bg-secondary'
              }`}
            >
              <span>{c.customerName.length > 12 ? c.customerName.slice(0, 12) + '…' : c.customerName}</span>
              <button
                onClick={(e) => { e.stopPropagation(); removeConversation(c.customerId); }}
                className={`w-3.5 h-3.5 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity ${
                  c.customerId === activeConversationId ? 'hover:bg-primary-foreground/20' : 'hover:bg-muted'
                }`}
              >
                <X className="w-2.5 h-2.5" />
              </button>
            </button>
          ))}
        </div>
      )}

      {/* Active conversation or empty state */}
      {activeConv ? (
        <ConversationPanel conv={activeConv} />
      ) : (
        <div className="flex-1 flex flex-col items-center justify-center text-center px-6">
          <MessageCircle className="w-10 h-10 text-muted-foreground/20 mb-3" />
          <p className="text-sm font-medium text-muted-foreground">Ingen samtaler åbne</p>
          <p className="text-xs text-muted-foreground/60 mt-1">Åbn en sag for at starte en samtale</p>
        </div>
      )}
    </div>
  );
}
